// You can write more code here

import { Customization, EquipmentConfiguration } from '@/common/types/student';
import NpcCustomizations from '@/game/GameComponents/GameData/customizationsNPC.json';
import animationsConfig from '@/game/GameComponents/PhaserGame/Utils/animationsConfig.json';
import {
  AnimationUtils,
  Direction,
  IAnimationConfig,
} from '@/game/GameComponents/PhaserGame/Utils/AnimationUtils';
import {
  getNpcCustomizationFromId,
  getSpriteSheetPath,
} from '@/game/GameComponents/PhaserGame/Utils/CosmeticUtils';
import { performSlotHiding } from '@/game/GameComponents/PhaserGame/Utils/NpcUtils';

/* START OF COMPILED CODE */

export default class NPC extends Phaser.GameObjects.Container {
  constructor(scene: Phaser.Scene, x?: number, y?: number) {
    super(scene, x ?? 0, y ?? 0);

    this.setInteractive(
      new Phaser.Geom.Rectangle(-16, -16, 32, 32),
      Phaser.Geom.Rectangle.Contains,
    );

    // npc_complexion
    const npc_complexion = scene.add.sprite(0, 0, 'complexion-1', 0);
    npc_complexion.name = 'npc_complexion';
    npc_complexion.setOrigin(0.5, 0.69);
    this.add(npc_complexion);

    // npc_clothes
    const npc_clothes = scene.add.sprite(0, 0, 'blue-tank-top', 0);
    npc_clothes.name = 'npc_clothes';
    npc_clothes.setOrigin(0.5, 0.69);
    this.add(npc_clothes);

    // npc_pants
    const npc_pants = scene.add.sprite(0, 0, 'blue-frilly-skirt', 0);
    npc_pants.name = 'npc_pants';
    npc_pants.setOrigin(0.5, 0.69);
    this.add(npc_pants);

    // npc_shoes
    const npc_shoes = scene.add.sprite(0, 0, 'brown-shoes', 0);
    npc_shoes.name = 'npc_shoes';
    npc_shoes.setOrigin(0.5, 0.69);
    this.add(npc_shoes);

    // npc_hair
    const npc_hair = scene.add.sprite(0, 0, 'brown-bob', 0);
    npc_hair.name = 'npc_hair';
    npc_hair.setOrigin(0.5, 0.69);
    this.add(npc_hair);

    this.npc_complexion = npc_complexion;
    this.npc_clothes = npc_clothes;
    this.npc_pants = npc_pants;
    this.npc_shoes = npc_shoes;
    this.npc_hair = npc_hair;
    // awake handler
    this.scene.events.once('scene-awake', () => this.awake());

    /* START-USER-CTR-CODE */
    /* END-USER-CTR-CODE */
  }

  public npc_complexion: Phaser.GameObjects.Sprite;
  public npc_clothes: Phaser.GameObjects.Sprite;
  public npc_pants: Phaser.GameObjects.Sprite;
  public npc_shoes: Phaser.GameObjects.Sprite;
  public npc_hair: Phaser.GameObjects.Sprite;
  public npcId: number = 0;
  public npcLevelId: number = 0;

  /* START-USER-CODE */

  public configuration: Partial<EquipmentConfiguration> = {};
  animInterval?: Phaser.Time.TimerEvent;
  prevAnimationKey?: string;

  configure(configuration: Partial<EquipmentConfiguration>) {
    this.configuration = configuration;

    this.list.forEach((child: any) => {
      if (child.name !== 'npc_complexion') {
        child.setVisible(false);
      }
    });

    Object.keys(configuration).forEach((key: string) => {
      const customizationId =
        configuration[key as keyof EquipmentConfiguration];
      if (customizationId === undefined || customizationId === null) return;
      const customization = getNpcCustomizationFromId(customizationId);
      if (customization === undefined || customization === null) return;

      // Find the child game object with the corresponding slot.
      const slotObj = this.list.find(
        (child) => child.name === `npc_${customization.slot}`,
      );
      const slotSprite = slotObj as Phaser.GameObjects.Sprite;
      if (slotSprite as Phaser.GameObjects.Sprite) {
        slotSprite.setVisible(true);
        slotSprite.setTexture(getSpriteSheetPath(customization));
      }
    });

    // Do slot hiding for the equipped items.
    performSlotHiding(
      this,
      configuration,
      'npc_',
      NpcCustomizations as Customization[],
    );

    // The NPC has finished configuring its appearance.
  }

  awake() {}

  createAnimations() {
    if (!this.configuration) return;

    const createAnimation = function (
      scene: Phaser.Scene,
      spriteSheetPath: string,
      motion: keyof typeof animationsConfig,
    ) {
      if (scene.anims.exists(`${spriteSheetPath}-${motion}`)) {
        scene.anims.remove(`${spriteSheetPath}-${motion}`);
      }

      const AnimationConfig = animationsConfig[motion] as IAnimationConfig;

      const frameNumbers = AnimationConfig.frames;
      let frames;
      if (frameNumbers) {
        frames = scene.anims.generateFrameNumbers(`${spriteSheetPath}`, {
          frames: frameNumbers,
        });
      }
      if (!frames) {
        frames = scene.anims.generateFrameNumbers(`${spriteSheetPath}`, {
          start: AnimationConfig.start,
          end: AnimationConfig.end,
        });
      }

      scene.anims.create({
        key: `${spriteSheetPath}-${motion}`,
        frames,
        frameRate: animationsConfig[motion].frameRate,
        repeat: animationsConfig[motion].repeat,
      });
    };

    Object.keys(this.configuration).forEach((key: string) => {
      const customizationId =
        this.configuration[key as keyof EquipmentConfiguration];
      if (customizationId === undefined || customizationId === null) return;
      const customization = getNpcCustomizationFromId(customizationId);
      if (customization === undefined || customization === null) return;
      const spriteSheetPath = getSpriteSheetPath(customization);

      createAnimation(this.scene, spriteSheetPath, 'idle');
      createAnimation(this.scene, spriteSheetPath, 'idle-down');
      createAnimation(this.scene, spriteSheetPath, 'walk-right');
      createAnimation(this.scene, spriteSheetPath, 'walk-left');
      createAnimation(this.scene, spriteSheetPath, 'walk-up');
      createAnimation(this.scene, spriteSheetPath, 'walk-down');
      createAnimation(this.scene, spriteSheetPath, 'wave');
      createAnimation(this.scene, spriteSheetPath, 'meditate');
      createAnimation(this.scene, spriteSheetPath, 'sing');
      createAnimation(this.scene, spriteSheetPath, 'defeated');
      createAnimation(this.scene, spriteSheetPath, 'hurt');
      createAnimation(this.scene, spriteSheetPath, 'attack');
      createAnimation(this.scene, spriteSheetPath, 'tug-collar');
      createAnimation(this.scene, spriteSheetPath, 'shifty-eyes');
      createAnimation(this.scene, spriteSheetPath, 'blink-turn-head');
    });
  }

  setIdleFacingPlayer(player: Phaser.Physics.Arcade.Body) {
    const xDelta = this.x - player.x;
    const yDelta = this.y - player.y + 10; // idk why + 10, it just makes the numbers seem right.

    const yMargin = 15; // How far away we have to be on y axis to make characters face up/down.

    if (xDelta < 0) {
      AnimationUtils.setIdle(this, Direction.LEFT);
    } else {
      AnimationUtils.setIdle(this, Direction.RIGHT);
    }
    if (Math.abs(yDelta) > yMargin) {
      const dir = yDelta > 0 ? Direction.UP : Direction.DOWN;
      AnimationUtils.setIdle(this, dir);
    }
  }

  handleGameCompleted() {
    AnimationUtils.successFeedback(this);
  }

  /* END-USER-CODE */
}

/* END OF COMPILED CODE */

// You can write more code here
